import type { BaseSyntheticEvent, ComponentPropsWithoutRef } from "react";
import { Controller, FormProvider } from "react-hook-form";
import type { ControllerProps, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { FormFieldStateContext } from "@erp/ui";

export interface FormProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> extends Omit<ComponentPropsWithoutRef<"form">, "onSubmit"> {
  /** The `useZodForm` return — provided to all nested `<FormField>`s. */
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  /** Typically the `onSubmit` returned by `useFormSubmit`. */
  onSubmit?: (event?: BaseSyntheticEvent) => unknown;
}

/**
 * Form context provider + the `<form>` element itself:
 *
 * ```tsx
 * const form = useZodForm(schema, { defaultValues });
 * const { onSubmit } = useFormSubmit(form, mutationFn);
 * <Form form={form} onSubmit={onSubmit} className="flex flex-col gap-4">
 *   <FormField name="email" render={…} />
 * </Form>
 * ```
 *
 * Native validation is disabled (`noValidate`) — zod owns validation.
 */
export function Form<
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>({ form, onSubmit, children, ...props }: FormProps<TFieldValues, TContext, TTransformedValues>) {
  return (
    <FormProvider {...form}>
      <form
        noValidate
        onSubmit={onSubmit === undefined ? undefined : (event) => void onSubmit(event)}
        {...props}
      >
        {children}
      </form>
    </FormProvider>
  );
}

export type FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> = ControllerProps<TFieldValues, TName, TTransformedValues>;

/**
 * RHF Controller bound to @erp/ui's presentational form primitives. It
 * publishes the field's error state through `FormFieldStateContext`, so inside
 * `render` the primitives wire themselves up automatically: `FormLabel` turns
 * destructive, `FormControl` injects `id` / `aria-invalid` /
 * `aria-describedby`, and `FormMessage` renders the localized error text.
 * `control` is optional inside `<Form>` (taken from context).
 *
 * ```tsx
 * <FormField
 *   name="email"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel required>{t("auth.email")}</FormLabel>
 *       <FormControl>
 *         <Input type="email" {...field} />
 *       </FormControl>
 *       <FormMessage />
 *     </FormItem>
 *   )}
 * />
 * ```
 */
export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({ render, ...props }: FormFieldProps<TFieldValues, TName, TTransformedValues>) {
  return (
    <Controller<TFieldValues, TName, TTransformedValues>
      {...props}
      render={(renderProps) => (
        <FormFieldStateContext.Provider
          value={{
            invalid: renderProps.fieldState.invalid,
            errorMessage: renderProps.fieldState.error?.message,
          }}
        >
          {render(renderProps)}
        </FormFieldStateContext.Provider>
      )}
    />
  );
}
